class CLWSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.clw-slider__track');
    if (!this.track) return;

    const progressId = this.dataset.progressId;
    this.progressEl = progressId ? document.getElementById(progressId) : null;
    this.thumb = this.progressEl ? this.progressEl.querySelector('.clw-progress__thumb') : null;
    this.progressTrack = this.progressEl ? this.progressEl.querySelector('.clw-progress__track') : null;
    this.isDragging = false;

    this.querySelectorAll('.clw-slider__nav-btn--prev').forEach((btn) =>
      btn.addEventListener('click', () => this.slide(-1))
    );
    this.querySelectorAll('.clw-slider__nav-btn--next').forEach((btn) =>
      btn.addEventListener('click', () => this.slide(1))
    );

    if (this.progressEl) {
      this.progressEl.querySelectorAll('.clw-progress__btn--prev').forEach((btn) =>
        btn.addEventListener('click', () => this.slide(-1))
      );
      this.progressEl.querySelectorAll('.clw-progress__btn--next').forEach((btn) =>
        btn.addEventListener('click', () => this.slide(1))
      );
    }

    this.track.addEventListener('scroll', () => {
      this.syncProgress();
      this.updateNavState();
    }, { passive: true });
    this.initDrag();
    this.initTrackClick();
    this.syncProgress();
    this.updateNavState();
    this.updateVisibility();

    this.resizeObserver = new ResizeObserver(() => {
      this.syncProgress();
      this.updateNavState();
      this.updateVisibility();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  updateVisibility() {
    const scrollable = this.track.scrollWidth > this.track.clientWidth + 1;
    this.querySelectorAll('.clw-slider__nav-btn--prev, .clw-slider__nav-btn--next').forEach((btn) => {
      btn.style.display = scrollable ? '' : 'none';
    });
    if (this.progressEl) this.progressEl.style.display = scrollable ? '' : 'none';
  }

  syncProgress() {
    if (this.isDragging || !this.thumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const ratio = clientWidth / scrollWidth;
    const thumbWidthPct = ratio * 100;
    const maxLeftPct = 100 - thumbWidthPct;
    const scrollRatio = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 0;
    this.thumb.style.width = thumbWidthPct + '%';
    this.thumb.style.left = scrollRatio * maxLeftPct + '%';
  }

  updateNavState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 0;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    this.querySelectorAll('.clw-slider__nav-btn--prev').forEach((btn) => { btn.disabled = atStart; });
    this.querySelectorAll('.clw-slider__nav-btn--next').forEach((btn) => { btn.disabled = atEnd; });

    if (this.progressEl) {
      this.progressEl.querySelectorAll('.clw-progress__btn--prev').forEach((btn) => { btn.disabled = atStart; });
      this.progressEl.querySelectorAll('.clw-progress__btn--next').forEach((btn) => { btn.disabled = atEnd; });
    }
  }

  initDrag() {
    if (!this.thumb || !this.progressTrack) return;

    let startX = 0;
    let startThumbLeftPx = 0;

    const getThumbLeftPx = () => {
      const pct = parseFloat(this.thumb.style.left) || 0;
      return (pct / 100) * this.progressTrack.offsetWidth;
    };

    const onDragStart = (e) => {
      this.isDragging = true;
      startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      startThumbLeftPx = getThumbLeftPx();
      this.thumb.style.transition = 'none';
      this.track.style.scrollSnapType = 'none';
      e.preventDefault();
    };

    const onDragMove = (e) => {
      if (!this.isDragging) return;
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - startX;
      const trackWidth = this.progressTrack.offsetWidth;
      const thumbWidth = this.thumb.offsetWidth;
      const maxLeftPx = trackWidth - thumbWidth;

      const newLeftPx = Math.max(0, Math.min(maxLeftPx, startThumbLeftPx + deltaX));
      const newLeftPct = (newLeftPx / trackWidth) * 100;

      this.thumb.style.left = newLeftPct + '%';

      const scrollRatio = maxLeftPx > 0 ? newLeftPx / maxLeftPx : 0;
      const { scrollWidth, clientWidth } = this.track;
      this.track.scrollLeft = scrollRatio * (scrollWidth - clientWidth);
    };

    const onDragEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.thumb.style.transition = '';
      this.track.style.scrollSnapType = '';
      const item = this.track.querySelector('.clw-slider__item');
      if (item) {
        const gap = parseFloat(window.getComputedStyle(this.track).gap) || 0;
        const stride = item.offsetWidth + gap;
        const nearest = Math.round(this.track.scrollLeft / stride);
        this.track.scrollTo({ left: nearest * stride, behavior: 'smooth' });
      }
    };

    this.thumb.addEventListener('mousedown', onDragStart);
    this.thumb.addEventListener('touchstart', onDragStart, { passive: false });
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('touchmove', onDragMove, { passive: true });
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchend', onDragEnd);
  }

  initTrackClick() {
    if (!this.progressTrack || !this.thumb) return;

    this.progressTrack.addEventListener('click', (e) => {
      if (this.thumb.contains(e.target)) return;

      const rect = this.progressTrack.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = this.progressTrack.offsetWidth;
      const thumbWidth = this.thumb.offsetWidth;
      const maxLeftPx = trackWidth - thumbWidth;

      const newLeftPx = Math.max(0, Math.min(maxLeftPx, clickX - thumbWidth / 2));
      const scrollRatio = maxLeftPx > 0 ? newLeftPx / maxLeftPx : 0;
      const { scrollWidth, clientWidth } = this.track;
      this.track.scrollTo({ left: scrollRatio * (scrollWidth - clientWidth), behavior: 'smooth' });
    });
  }

  slide(direction) {
    const item = this.track.querySelector('.clw-slider__item');
    if (!item) return;
    const gap = parseFloat(window.getComputedStyle(this.track).gap) || 0;
    this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
  }
}

customElements.define('clw-slider', CLWSlider);
